import React, { useEffect, useRef, useCallback } from 'react';
import { Platform } from 'react-native';
import { useAppLayout, useExpenseTrackerInset, EXPENSE_TRACKER_ANIM_MS, EXPENSE_TRACKER_ANIM_EASING } from '../contexts/AppLayoutContext';

const ATTACH_DELAY_MS = 150;
const REATTACH_DEBOUNCE_MS = 300;
const TOP_HOVER_ZONE_PX = 32;
const SCROLL_DELTA = 6;

function flattenStyle(style) {
  const raw = Array.isArray(style)
    ? Object.assign({}, ...style.map((s) => s || {}))
    : (style || {});
  if (raw.paddingHorizontal !== undefined) {
    raw.paddingLeft = raw.paddingHorizontal;
    raw.paddingRight = raw.paddingHorizontal;
    delete raw.paddingHorizontal;
  }
  if (raw.paddingVertical !== undefined) {
    raw.paddingTop = raw.paddingVertical;
    raw.paddingBottom = raw.paddingVertical;
    delete raw.paddingVertical;
  }
  return raw;
}

function collectScrollTargets() {
  const targets = new Set();

  const scrollView = document.getElementById('scroll-view');
  if (scrollView) targets.add(scrollView);

  document.querySelectorAll('[data-app-scroll="true"]').forEach((el) => targets.add(el));
  targets.add(window);

  return [...targets];
}

export const HideOnScroll = ({ children, style, enabled = true }) => {
  const containerRef = useRef(null);
  const hoverZoneRef = useRef(null);
  const lastScrollTop = useRef(new Map());
  const visible = useRef(true);
  const hoveringNav = useRef(false);
  const hoveringTopZone = useRef(false);
  const ticking = useRef(false);
  const attachedEls = useRef(new Map());
  const { setNavVisible } = useAppLayout();
  const expenseInset = useExpenseTrackerInset();

  const show = useCallback(() => {
    if (!visible.current && containerRef.current) {
      visible.current = true;
      containerRef.current.style.transform = 'translateY(0)';
    }
    setNavVisible(true);
    if (hoverZoneRef.current) {
      hoverZoneRef.current.style.pointerEvents = 'none';
    }
  }, [setNavVisible]);

  const hide = useCallback(() => {
    if (visible.current && !hoveringNav.current && !hoveringTopZone.current && containerRef.current) {
      visible.current = false;
      containerRef.current.style.transform = 'translateY(-110%)';
      setNavVisible(false);
    }
    if (hoverZoneRef.current) {
      hoverZoneRef.current.style.pointerEvents = 'auto';
    }
  }, [setNavVisible]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !enabled) {
      setNavVisible(true);
      return;
    }

    const handleScrollPosition = (el, top) => {
      const last = lastScrollTop.current.get(el) ?? top;

      // Only reveal on deliberate scroll-up — not from resting at the top or layout noise.
      if (top < last - SCROLL_DELTA) {
        show();
      } else if (top > last + SCROLL_DELTA) {
        hide();
      }

      lastScrollTop.current.set(el, top);
    };

    const onScroll = (e) => {
      if (ticking.current) return;
      ticking.current = true;
      const target = e.currentTarget;
      window.requestAnimationFrame(() => {
        if (!target) {
          ticking.current = false;
          return;
        }
        const top =
          target === window
            ? window.scrollY || document.documentElement?.scrollTop || 0
            : target.scrollTop || 0;
        handleScrollPosition(target, top);
        ticking.current = false;
      });
    };

    const detachAll = () => {
      attachedEls.current.forEach((_, el) => {
        if (el === window) {
          window.removeEventListener('scroll', onScroll);
        } else {
          el.removeEventListener('scroll', onScroll);
        }
      });
      attachedEls.current.clear();
    };

    const attachTo = (el) => {
      if (!el || attachedEls.current.has(el)) return;
      el.addEventListener('scroll', onScroll, { passive: true });
      attachedEls.current.set(el, el === window ? 0 : (el.scrollTop || 0));
    };

    const detect = () => {
      const targets = collectScrollTargets();
      const next = new Set(targets);
      attachedEls.current.forEach((_, el) => {
        if (!next.has(el)) {
          if (el === window) {
            window.removeEventListener('scroll', onScroll);
          } else {
            el.removeEventListener('scroll', onScroll);
          }
          attachedEls.current.delete(el);
          lastScrollTop.current.delete(el);
        }
      });
      targets.forEach(attachTo);
    };

    const initTimer = setTimeout(detect, ATTACH_DELAY_MS);

    let debounceTimer = null;
    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(detect, REATTACH_DEBOUNCE_MS);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const reattachInterval = window.setInterval(detect, 2500);

    return () => {
      clearTimeout(initTimer);
      clearTimeout(debounceTimer);
      clearInterval(reattachInterval);
      observer.disconnect();
      detachAll();
      setNavVisible(true);
    };
  }, [enabled, setNavVisible, show, hide]);

  if (Platform.OS !== 'web') {
    return children;
  }

  const raw = flattenStyle(style);

  if (!enabled) {
    return (
      <div
        style={{
          ...raw,
          position: 'fixed',
          top: 0,
          left: 0,
          right: expenseInset,
          zIndex: 1000,
          transition: `right ${EXPENSE_TRACKER_ANIM_MS}ms ${EXPENSE_TRACKER_ANIM_EASING}`,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          boxSizing: 'border-box',
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <>
      <div
        ref={hoverZoneRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: expenseInset,
          height: TOP_HOVER_ZONE_PX,
          zIndex: 1001,
          pointerEvents: 'none',
        }}
        onMouseEnter={() => {
          hoveringTopZone.current = true;
          show();
        }}
        onMouseLeave={() => {
          hoveringTopZone.current = false;
        }}
      />
      <div
        ref={containerRef}
        style={{
          ...raw,
          position: 'fixed',
          top: 0,
          left: 0,
          right: expenseInset,
          zIndex: 1000,
          transition: `transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), right ${EXPENSE_TRACKER_ANIM_MS}ms ${EXPENSE_TRACKER_ANIM_EASING}`,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          boxSizing: 'border-box',
        }}
        onMouseEnter={() => {
          hoveringNav.current = true;
          show();
        }}
        onMouseLeave={() => {
          hoveringNav.current = false;
        }}
      >
        {children}
      </div>
    </>
  );
};
